import { Buffer } from 'buffer'
import MP4Box, { type MP4ArrayBuffer, type MP4File, type MP4Info } from 'mp4box'
import ReadBlockFactory from './ReadBlockFactory'

export const GPMF_CODEC = 'gpmd'

interface ExtractOptions {
  progress?: (percent: number) => void
}

interface Timing {
  start?: Date
  videoDuration?: number
  frameDuration?: number
  samples?: Array<{ cts: number; duration: number }>
}

const explainMissingGpmfTrack = (videoData: MP4Info): string => {
  const presentTrackKinds = videoData.tracks
    .map((t) => t.type ?? t.codec)
    .filter((kind): kind is string => kind != null)
  const whatIsThere =
    presentTrackKinds.length > 0
      ? ` The file only contains ${presentTrackKinds.join(' and ')} tracks.`
      : ''
  return (
    'No GoPro telemetry track (GPMF) was found in this MP4, so it was not recorded by a GoPro or has been re-encoded by a video editor, which drops the telemetry.' +
    whatIsThere +
    ' If the video was exported together with a .csv or .gpx file, upload that file instead.'
  )
}

export async function extract(
  file: File,
  options: ExtractOptions = {}
): Promise<{ rawData: Buffer; timing: Timing }> {
  const mp4boxFile: MP4File = MP4Box.createFile(false)
  const timing: Timing = {}

  return await new Promise((resolve, reject) => {
    const readBlock = new ReadBlockFactory()

    mp4boxFile.onReady = (videoData: MP4Info) => {
      let trackId: number | undefined
      let nbSamples: number | undefined
      let foundVideo: boolean = false
      for (let i = 0; i < videoData.tracks.length; i++) {
        if (videoData.tracks[i].codec === GPMF_CODEC) {
          trackId = videoData.tracks[i].id
          nbSamples = videoData.tracks[i].nb_samples
          timing.start = videoData.tracks[i].created
          timing.start.setMinutes(
            timing.start.getMinutes() + timing.start.getTimezoneOffset()
          )
        } else if (
          !foundVideo &&
          (videoData.tracks[i].type === 'video' ||
            videoData.tracks[i].name === 'VideoHandler' ||
            videoData.tracks[i].track_height > 0)
        ) {
          if (videoData.tracks[i].type === 'video') foundVideo = true
          const vid = videoData.tracks[i]
          timing.videoDuration = vid.movie_duration / vid.movie_timescale
          timing.frameDuration = timing.videoDuration / vid.nb_samples
        }
      }
      if (trackId != null) {
        mp4boxFile.setExtractionOptions(trackId, null, {
          nbSamples: nbSamples ?? 0,
        })

        mp4boxFile.onSamples = (_id, _user, samples) => {
          readBlock.stop()
          const trackSize = samples.reduce(
            (size, sample) => size + sample.size,
            0
          )

          timing.samples = samples.map(({ cts, duration }) => ({
            cts,
            duration,
          }))

          const track = new Uint8Array(trackSize)
          let offset = 0
          for (const sample of samples) {
            track.set(sample.data, offset)
            offset += sample.size
          }

          resolve({ rawData: Buffer.from(track.buffer), timing })
        }
        mp4boxFile.start()
      } else {
        readBlock.stop()
        reject(new Error(explainMissingGpmfTrack(videoData)))
      }
    }

    const onParsedBuffer = (buffer: MP4ArrayBuffer, offset: number) => {
      if (buffer.byteLength === 0) {
        readBlock.stop()
        reject(new Error('File not compatible'))
        return
      }

      buffer.fileStart = offset

      mp4boxFile.appendBuffer(buffer)
    }
    readBlock.read(file, {
      update: options.progress,
      onParsedBuffer,
      mp4boxFile,
      onError: reject,
    })
  })
}
